import * as React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useFeriadosChile } from '@/hooks/useFeriadosChile';

/**
 * DatePickerInput — drop-in replacement for `<input type="date">`.
 *
 * Bloquea por defecto sábados/domingos y feriados chilenos (Carlos 2026-05-01).
 * El mes inicial es el de hoy (default sugerido por Carlos).
 *
 * Props:
 * - value: string ISO 'YYYY-MM-DD' | undefined
 * - onChange: (iso: string) => void
 * - placeholder?: string
 * - disabled?: boolean
 * - className?: string
 * - id?: string
 * - allowWeekends?: boolean   (default false: bloquea findes)
 * - allowFeriados?: boolean   (default false: bloquea feriados)
 * - 'data-testid'?: string
 */
export function DatePickerInput({
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  disabled = false,
  className,
  id,
  allowWeekends = false,
  allowFeriados = false,
  ...rest
}) {
  const date = React.useMemo(() => {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
  }, [value]);

  const { isBloqueado, getBloqueoReason, getFeriadoInfo } = useFeriadosChile();

  const display = date ? format(date, "EEE dd 'de' MMM yyyy", { locale: es }) : '';

  // Función de día deshabilitado usada por react-day-picker
  const disabledMatcher = React.useCallback((d) => {
    if (!d) return false;
    const wd = d.getDay();
    if (!allowWeekends && (wd === 0 || wd === 6)) return true;
    if (!allowFeriados && isBloqueado(d)) return true;
    return false;
  }, [allowWeekends, allowFeriados, isBloqueado]);

  // Modifiers para colorear feriados + findes
  const modifiers = React.useMemo(() => ({
    weekend: (d) => {
      const wd = d.getDay();
      return wd === 0 || wd === 6;
    },
    feriado: (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return !!getFeriadoInfo(`${y}-${m}-${day}`);
    },
  }), [getFeriadoInfo]);

  const modifiersClassNames = {
    weekend: 'text-gray-400 dark:text-gray-600',
    feriado: 'text-red-600 dark:text-red-400 font-semibold',
  };

  // Default month: el mes de `value` si existe; sino, hoy.
  const defaultMonth = date || new Date();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground',
            className,
          )}
          data-testid={rest['data-testid']}
          title={date ? getBloqueoReason(date) || '' : ''}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {display || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={defaultMonth}
          onSelect={(d) => {
            if (!d) {
              onChange?.('');
              return;
            }
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            onChange?.(`${y}-${m}-${day}`);
          }}
          locale={es}
          weekStartsOn={1}
          initialFocus
          disabled={disabledMatcher}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePickerInput;
